import { useMemo, useState } from 'react';
import { Button, SimpleGrid } from '@mantine/core';
import { HeaderContainer } from '@dealstack/frontend/shared/components/Documents/styled';
import { H5 } from '@dealstack/frontend/shared/components/Documents/UploadDocuments/styled';
import { EmptyState } from '@dealstack/frontend/shared/components/EmptyState/EmptyState';
import {
	StyledMultiFileDropZone,
	UnclassifiedContainer,
} from '@dealstack/frontend/shared/components/Documents/UploadDocuments/AddFileStep/styled';
import { useDocumentClassifiers } from '@dealstack/frontend/shared/components/Documents/UploadDocuments/AddFileStep/useDocumentClassifiers';
import { ACCEPT_FILES_TYPES } from '@dealstack/frontend/shared/components/MultiFileDropZone/MultiFileDropZone';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { DealClassifierRes } from '@dealstack/shared/lib/generated';
import { FileDropSkeletons } from '@dealstack/frontend/shared/components/Documents/UploadDocuments/AddFileStep/ClassifierDropZones/FileDropSkeletons';
import { Paginate } from '@dealstack/frontend/shared/components/Documents/UploadDocuments/components/Paginate';
import { Link } from 'react-router-dom';
import {
	DOCUMENT_LIBRARY_ELEMENT_ID,
	makeComplexDataTestId,
} from '@dealstack/shared/tests/testElementIds';

interface Props {
	dealId: string;
	handleProcessFile: ({
		dealClassifierId,
		classifierName,
		files,
	}: {
		dealClassifierId?: string;
		classifierName?: string;
		files: File[];
	}) => void;
}

export const ClassifierDropZones = ({ dealId, handleProcessFile }: Props) => {
	const tCapManage = useTranslationCapManage();
	const [pageIndex, setPageIndex] = useState(0);
	const batchSize = 8;
	const { documentClassifiers, isDocumentClassifiersLoading } = useDocumentClassifiers({
		dealId,
	});

	const batches = useMemo(() => {
		const chunks: DealClassifierRes[][] = [];
		if (!documentClassifiers) {
			return [];
		}

		for (let i = 0; i < documentClassifiers.length; i += batchSize) {
			chunks.push(documentClassifiers.slice(i, i + batchSize));
		}
		return chunks;
	}, [documentClassifiers]);

	const currentBatch = batches[pageIndex] || [];

	if (!isDocumentClassifiersLoading && !documentClassifiers?.length) {
		return (
			<EmptyState
				dataTestId={DOCUMENT_LIBRARY_ELEMENT_ID.UPLOAD_CLASSIFIERS_EMPTY_STATE}
				title={tCapManage('documents.classifiers.not_found.heading')}
			>
				<Link to={`/deal/${dealId}/settings/classifiers`}>
					<Button>{tCapManage('documents.classifiers.not_found.button')}</Button>
				</Link>
			</EmptyState>
		);
	}

	return (
		<>
			<HeaderContainer id="upload-by-classifier-header">
				<H5>{tCapManage('document.upload_state.classifier.header')}</H5>
				<Paginate data={batches} onClick={setPageIndex} pageIndex={pageIndex} />
			</HeaderContainer>
			<SimpleGrid cols={4}>
				{isDocumentClassifiersLoading && <FileDropSkeletons />}
				{!isDocumentClassifiersLoading &&
					currentBatch?.map((classifier, index) => (
						<StyledMultiFileDropZone
							key={`${classifier}_${index}`}
							data-testid={makeComplexDataTestId(
								DOCUMENT_LIBRARY_ELEMENT_ID.UPLOAD_CLASSIFIER_DROPZONE,
								classifier.name
							)}
							footerText={classifier.name}
							size="STD"
							disabled={false}
							onDropFile={async (files) => {
								handleProcessFile({
									dealClassifierId: classifier.dealClassifierId,
									classifierName: classifier.name,
									files,
								});
							}}
							multiple
							accept={{
								...ACCEPT_FILES_TYPES.imagesAndPdf,
								...ACCEPT_FILES_TYPES.excel,
								...ACCEPT_FILES_TYPES.word,
							}}
						/>
					))}
			</SimpleGrid>
			<HeaderContainer id="upload-by-classifier-header">
				<H5>{tCapManage('document.upload_state.classifier.unclassified.header')}</H5>
			</HeaderContainer>
			<UnclassifiedContainer>
				<StyledMultiFileDropZone
					data-testid={DOCUMENT_LIBRARY_ELEMENT_ID.UPLOAD_UNCLASSIFIED_DROPZONE}
					minWidth="100%"
					size="STD"
					disabled={false}
					onDropFile={async (files) => {
						handleProcessFile({
							files,
							classifierName: 'Unclassified',
						});
					}}
					multiple
					accept={{
						...ACCEPT_FILES_TYPES.imagesAndPdf,
						...ACCEPT_FILES_TYPES.excel,
						...ACCEPT_FILES_TYPES.word,
					}}
				/>
			</UnclassifiedContainer>
		</>
	);
};
